import { useState, useCallback } from 'react';
import { Send, Check } from 'lucide-react';
import StepIdentificacion from './steps/StepIdentificacion';
import StepLogistica from './steps/StepLogistica';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, label: 'Identificación', title: 'Su perfil y su empresa' },
  { num: 2, label: 'Confirmación', title: 'Finalización y confidencialidad' },
];

const initialFormData = {
  nombre: '',
  cargo: '',
  email: '',
  telefono: '',
  empresa: '',
  provinciaPais: '',
  sector: '',
  facturacion: '',
  empleados: '',
  referido: '',
  urgencia: '',
  disponibilidad: '',
  aceptaNDA: false,
};

export default function EmpresaWizard() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submissionId] = useState(() => crypto.randomUUID());
  const [slideDir, setSlideDir] = useState('right');

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const validateStep = (stepIndex) => {
    const errs = {};
    if (stepIndex === 0) {
      if (!formData.nombre.trim()) errs.nombre = 'Campo obligatorio';
      if (!formData.cargo.trim()) errs.cargo = 'Campo obligatorio';
      if (!formData.email.trim()) errs.email = 'Campo obligatorio';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Email no válido';
      if (!formData.telefono.trim()) errs.telefono = 'Campo obligatorio';
      if (!formData.empresa.trim()) errs.empresa = 'Campo obligatorio';
      if (!formData.sector) errs.sector = 'Seleccione un sector';
      if (!formData.facturacion) errs.facturacion = 'Seleccione un rango';
    } else if (stepIndex === 1) {
      if (!formData.urgencia) errs.urgencia = 'Seleccione la urgencia';
      if (!formData.disponibilidad) errs.disponibilidad = 'Seleccione la disponibilidad';
      if (!formData.aceptaNDA) errs.aceptaNDA = 'Debe aceptar los términos';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    setSlideDir('right');
    setStep(1);
  };

  const goPrev = () => {
    setSlideDir('left');
    setStep(0);
  };

  const handleSubmit = async () => {
    if (!validateStep(1)) return;
    if (submitted) return;
    setSubmitting(true);
    setSubmitError(false);
    try {
      const res = await fetch(`${BACKEND_URL}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submission_id: submissionId,
          form_type: 'empresa',
          payload: formData,
          source: 'conadvy_landing',
          page_path: window.location.pathname,
          submitted_at: new Date().toISOString(),
        }),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setSubmitError(true);
      }
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.error('Error submitting mandato:', e);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-[660px] mx-auto px-6">
        <div
          data-testid="wizard-success"
          className="bg-white border border-[#E4E6EA] rounded-sm p-12 md:p-16 text-center"
        >
          <div className="w-20 h-20 rounded-full border-2 border-[#C58C40] flex items-center justify-center mx-auto mb-8">
            <Check size={32} className="text-[#C58C40]" />
          </div>
          <h2 className="text-2xl md:text-3xl font-normal text-[#00163E] mb-4">
            Solicitud recibida
          </h2>
          <p className="text-[#00163E]/70 text-base leading-relaxed max-w-md mx-auto mb-3">
            Nuestro equipo revisará su mandato bajo estricta confidencialidad.
          </p>
          <p className="text-[#8A9BB1] text-sm">
            Le contactaremos en un plazo máximo de 48 horas.
          </p>
        </div>
      </div>
    );
  }

  const stepComponents = [
    <StepIdentificacion data={formData} errors={errors} onChange={updateField} />,
    <StepLogistica data={formData} errors={errors} onChange={updateField} />,
  ];

  return (
    <div className="max-w-[660px] mx-auto px-6" data-testid="empresa-wizard">
      {/* Minimal progress bar */}
      <div className="mb-8">
        <div className="flex gap-2 mb-3">
          {STEPS.map((_, i) => (
            <div
              key={i}
              data-testid={`wizard-progress-segment-${i}`}
              className={`flex-1 h-[2px] rounded-full transition-colors duration-500 ${
                i <= step ? 'bg-[#C58C40]' : 'bg-[#00163E]/10'
              }`}
            />
          ))}
        </div>
        <p className="text-center text-[#8A9BB1] text-xs tracking-wide">
          Paso {step + 1} de {STEPS.length} — {STEPS[step].title}
        </p>
      </div>

      {/* Card */}
      <div className="bg-white border border-[#E4E6EA] rounded-sm overflow-hidden">
        <div className="px-8 md:px-10 pt-8 md:pt-10 pb-6 border-b border-[#E4E6EA]">
          <span className="text-[#C58C40] text-xs font-medium tracking-[0.2em] uppercase">
            Paso {step + 1} — {STEPS[step].label}
          </span>
          <h3
            data-testid="wizard-step-title"
            className="text-xl md:text-2xl font-normal text-[#00163E] mt-2"
          >
            {STEPS[step].title}
          </h3>
        </div>

        <div className="relative overflow-hidden">
          <div
            key={step}
            className={`px-8 md:px-10 py-8 md:py-10 wizard-step-enter ${
              slideDir === 'right' ? 'wizard-slide-from-right' : 'wizard-slide-from-left'
            }`}
          >
            {stepComponents[step]}
          </div>
        </div>

        <div className="px-8 md:px-10 py-5 border-t border-[#E4E6EA] flex items-center justify-between">
          {step > 0 ? (
            <button
              onClick={goPrev}
              data-testid="wizard-prev-btn"
              className="flex items-center gap-2 text-[#00163E]/50 hover:text-[#00163E] text-sm tracking-wide transition-colors"
            >
              <span>Anterior</span>
            </button>
          ) : (
            <div />
          )}

          {step === 0 ? (
            <button
              onClick={goNext}
              data-testid="wizard-next-btn"
              className="flex items-center gap-2 bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-8 py-3 text-sm tracking-[0.1em] uppercase"
            >
              <span>Siguiente</span>
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="wizard-submit-btn"
              className="flex items-center gap-2 bg-[#C58C40] text-white hover:bg-[#b07b35] transition-colors duration-300 rounded-sm px-8 py-3 text-sm tracking-[0.1em] uppercase disabled:opacity-50"
            >
              <Send size={16} />
              <span>{submitting ? 'Enviando...' : 'Enviar Mandato'}</span>
            </button>
          )}
        </div>
      </div>

      {submitError && (
        <p data-testid="wizard-submit-error" className="text-center text-red-500 text-sm mt-4">
          Error al enviar el mandato. Por favor, inténtelo de nuevo.
        </p>
      )}
    </div>
  );
}
